import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, CheckCircle, XCircle, Clock, Download } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { DatePicker } from "@/components/ui/date-picker";

function fmt(iso) {
  if (!iso) return "—";
  return formatDateTime(iso);
}

// Escape a value for a CSV cell (quote if it contains a comma, quote or newline).
function csvCell(v) {
  const s = String(v ?? "");
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function duration(seconds) {
  if (seconds == null) return "Active";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function DriverLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDriver, setFilterDriver] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/driver-logs");
        setLogs(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const drivers = [...new Set(logs.map((l) => l.driver_name))].sort();

  const visible = logs.filter((l) => {
    if (filterDriver && l.driver_name !== filterDriver) return false;
    if (fromDate && (l.date || "") < fromDate) return false;
    if (toDate && (l.date || "") > toDate) return false;
    return true;
  });

  const downloadCsv = () => {
    if (visible.length === 0) { toast.error("No logs in the selected range to export."); return; }
    // Build a stable column per precheck item — the union of all item labels seen
    // across the exported logs, ordered by first appearance. Each driver then gets
    // one cell per item showing exactly what they accepted (Yes / No / N/A).
    const itemLabels = [];
    const seenLabels = new Set();
    visible.forEach((l) => {
      (l.precheck_items || []).forEach((it) => {
        if (it?.label && !seenLabels.has(it.label)) { seenLabels.add(it.label); itemLabels.push(it.label); }
      });
    });
    const header = ["Driver", "Date", "Precheck", "Comment", ...itemLabels, "Login", "Logout", "Duration"];
    const rows = visible.map((l) => {
      const answerByLabel = {};
      (l.precheck_items || []).forEach((it) => { if (it?.label) answerByLabel[it.label] = it.answer ?? ""; });
      return [
        l.driver_name || "",
        l.date || "",
        l.precheck_all_passed ? "All Passed" : "Has Failures",
        l.precheck_comment || "",
        ...itemLabels.map((label) => answerByLabel[label] ?? ""),
        fmt(l.login_at),
        fmt(l.logout_at),
        duration(l.total_seconds),
      ];
    });
    const csv = [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
    // Prepend BOM so Excel reads UTF-8 correctly.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const suffix = (fromDate || toDate) ? `_${fromDate || "start"}_to_${toDate || "end"}` : "";
    a.href = url;
    a.download = `driver-logs${suffix}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  return (
    <div data-testid="driver-logs-page">
      <div className="flex items-center gap-3 mb-1">
        <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b" }}>
          <ClipboardCheck size={18} />
        </span>
        <h1 className="brand-title text-2xl md:text-3xl">Driver Logs</h1>
      </div>
      <p className="text-white/60 text-sm mb-6">Daily precheck completion and login duration for each driver.</p>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Driver</label>
          <select
            value={filterDriver}
            onChange={(e) => setFilterDriver(e.target.value)}
            className="df-input-rect text-sm"
            style={{ maxWidth: 220 }}
          >
            <option value="" className="bg-[#15151f]">All Drivers</option>
            {drivers.map((d) => (
              <option key={d} value={d} className="bg-[#15151f]">{d}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-widest text-white/40 mb-1">From</label>
          <DatePicker value={fromDate} onChange={setFromDate} max={toDate || undefined}
            className="text-sm" style={{ maxWidth: 170 }} data-testid="logs-from" />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-widest text-white/40 mb-1">To</label>
          <DatePicker value={toDate} onChange={setToDate} min={fromDate || undefined}
            className="text-sm" style={{ maxWidth: 170 }} data-testid="logs-to" />
        </div>
        <button
          onClick={downloadCsv}
          className="px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2"
          style={{ background: "rgba(25,230,200,0.12)", color: "#19e6c8" }}
          data-testid="download-logs-csv"
        >
          <Download size={14} /> Download CSV
        </button>
        {(filterDriver || fromDate || toDate) && (
          <button
            onClick={() => { setFilterDriver(""); setFromDate(""); setToDate(""); }}
            className="px-3 py-2 rounded-xl text-xs font-semibold text-white/50 hover:text-white bg-white/5 hover:bg-white/10"
          >
            Clear filters
          </button>
        )}
      </div>

      {loading && (
        <p className="text-white/50 text-sm">Loading…</p>
      )}

      {!loading && visible.length === 0 && (
        <p className="text-white/50 text-sm">No driver logs found.</p>
      )}

      {!loading && visible.length > 0 && (
        <div className="rounded-2xl border border-white/10 overflow-hidden">
          {/* Header */}
          <div className="hidden md:grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr] gap-4 px-5 py-3 text-xs font-semibold uppercase tracking-widest text-white/40"
            style={{ background: "rgba(255,255,255,0.03)" }}>
            <span>Driver</span>
            <span>Date</span>
            <span>Precheck</span>
            <span>Login</span>
            <span>Logout</span>
            <span>Duration</span>
          </div>

          <div className="divide-y divide-white/5">
            {visible.map((log) => (
              <div key={log.id}
                className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr] gap-2 md:gap-4 px-5 py-4 items-start md:items-center hover:bg-white/[0.02] transition-colors">
                {/* Driver */}
                <div>
                  <p className="text-sm font-semibold text-white">{log.driver_name}</p>
                  <p className="text-xs text-white/40 md:hidden">{log.date}</p>
                </div>
                {/* Date */}
                <p className="text-sm text-white/70 hidden md:block">{log.date}</p>
                {/* Precheck */}
                <div className="flex items-center gap-1.5">
                  {log.precheck_all_passed ? (
                    <>
                      <CheckCircle size={14} className="text-green-400 shrink-0" />
                      <span className="text-xs font-semibold text-green-400">All Passed</span>
                    </>
                  ) : (
                    <>
                      <XCircle size={14} className="text-red-400 shrink-0" />
                      <span className="text-xs font-semibold text-red-400">Has Failures</span>
                    </>
                  )}
                </div>
                {/* Comment */}
                {log.precheck_comment && (
                  <div className="md:col-span-5 md:col-start-2 -mt-1 md:mt-0 text-xs text-white/40 italic pl-5">
                    "{log.precheck_comment}"
                  </div>
                )}
                {/* Login */}
                <p className="text-sm text-white/70">{fmt(log.login_at)}</p>
                {/* Logout */}
                <p className="text-sm text-white/70">{fmt(log.logout_at)}</p>
                {/* Duration */}
                <div className="flex items-center gap-1.5">
                  <Clock size={13} className="text-white/30 shrink-0" />
                  <span className={`text-sm font-semibold ${log.logout_at ? "text-white/80" : "text-[#19e6c8]"}`}>
                    {duration(log.total_seconds)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-white/30 mt-4">
        {visible.length} record{visible.length !== 1 ? "s" : ""}
        {(filterDriver || fromDate || toDate) ? " (filtered)" : ""}
      </p>
    </div>
  );
}
